/** Preset winners are compiled once per release; arbitrary deadlines retain exact evaluation. */
import type { Cadence, Manifest, SessionPlan } from '../types.ts';
import { composeSession, compositionSourceKey } from './composition.ts';
import { COMPOSITION_POLICY_VERSION } from './composition-policy.ts';
import { FLOW_REVIEW_SHA256 } from './flow-review.ts';
import { planFingerprint } from './plan.ts';
export const PRESET_SECONDS = [900,1200,1500,1800,2700,3600,5400];
const CADENCES:Cadence[]=['opening','occasional','frequent'];
export interface CompositionCatalog {
  binding:string;
  sources:Map<string,string>;
  cells:Map<string,Map<string,{plan:SessionPlan;recipe:string}>>;
}
const catalogs=new WeakMap<Manifest,CompositionCatalog>();
const cellKey=(targetSec:number,cadence:Cadence)=>`${targetSec}/${cadence}`;
function binding(manifest:Manifest):string {
  return JSON.stringify([manifest.version,manifest.generatedAt,manifest.releaseDigest,manifest.manifestDigest,
    COMPOSITION_POLICY_VERSION,FLOW_REVIEW_SHA256]);
}
function empty(manifest:Manifest):CompositionCatalog {
  return {binding:binding(manifest),sources:new Map(manifest.recordings.map(r=>[r.id,compositionSourceKey(r)])),cells:new Map()};
}
function append(catalog:CompositionCatalog,recording:Manifest['recordings'][number]){
  for(const targetSec of PRESET_SECONDS)for(const cadence of CADENCES){
    const key=cellKey(targetSec,cadence),cell=catalog.cells.get(key)??new Map();
    const result=composeSession(recording,targetSec,cadence);
    if(result.status==='conforming')cell.set(recording.id,{plan:result.plan,recipe:JSON.stringify(planFingerprint(result.plan,recording))});
    catalog.cells.set(key,cell);
  }
}
export function compileCompositionCatalog(manifest:Manifest):CompositionCatalog {
  const catalog=empty(manifest);for(const rec of manifest.recordings)append(catalog,rec);
  catalogs.set(manifest,catalog);return catalog;
}
export async function prepareCompositionCatalog(manifest:Manifest):Promise<void>{
  const catalog=empty(manifest);
  for(const rec of manifest.recordings){append(catalog,rec);await new Promise<void>(resolve=>setTimeout(resolve,0));}
  catalogs.set(manifest,catalog);
}
export function queryCompositionCatalog(manifest:Manifest,targetSec:number,cadence:Cadence,toleranceSec=30,desiredSpeechRatio?:number){
  if(toleranceSec!==30||desiredSpeechRatio!==undefined)return null;
  const catalog=catalogs.get(manifest);
  if(!catalog||catalog.binding!==binding(manifest)||catalog.sources.size!==manifest.recordings.length)return null;
  if(manifest.recordings.some(r=>catalog.sources.get(r.id)!==compositionSourceKey(r)))return null;
  return catalog.cells.get(cellKey(targetSec,cadence))??null;
}
